/**
 * Which outgoing links may carry the anchor, and what they become.
 *
 * Pure and browser-free so the awkward cases are testable: a host that merely
 * ENDS with the declared name must not match, a `mailto:` must be left alone,
 * and a link that already has a query string or a hash has to keep both. The
 * DOM side lives in cross-domain.ts.
 */

/** The parameter that carries the anchor. Read on arrival, then removed. */
export const CROSS_DOMAIN_PARAM = "tst_x";

/**
 * Exact host, or a subdomain of it.
 *
 * On the dot, so `notmyshop.nl` cannot pass as `myshop.nl`. Getting that wrong
 * would hand a visitor id to a domain the customer never declared, which is the
 * one thing this feature must not do.
 */
export function matchesDeclaredHost(hostname: string, hosts: ReadonlyArray<string>): boolean {
  const host = hostname.toLowerCase();
  for (const declared of hosts) {
    const candidate = declared.toLowerCase().trim();
    if (!candidate) continue;
    if (host === candidate) return true;
    if (host.endsWith("." + candidate)) return true;
  }
  return false;
}

/**
 * The decorated link, or null when this link is none of our business.
 *
 * `base` is the page the link sits on: it resolves relative hrefs and it is
 * what tells us the link leaves this domain at all.
 */
export function decorateHref(
  href: string,
  hosts: ReadonlyArray<string>,
  anchor: string,
  base: string,
): string | null {
  if (!href || hosts.length === 0 || !anchor) return null;

  let url: URL;
  let here: URL;
  try {
    here = new URL(base);
    url = new URL(href, base);
  } catch {
    return null;
  }
  // mailto:, tel:, javascript: and friends. They have no host to compare and
  // rewriting them would break the link.
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.hostname === here.hostname) return null;
  if (!matchesDeclaredHost(url.hostname, hosts)) return null;
  if (url.searchParams.get(CROSS_DOMAIN_PARAM)) return href;

  url.searchParams.set(CROSS_DOMAIN_PARAM, anchor);
  return url.toString();
}
